var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, FormControlLabel, Radio, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { DifferenceMode, StatusPermission } from '../../enum';
import { REPORT_ROUTER_URL } from '../../router/constants';
import { useSummaryChunkSize, useSummaryReportAPI } from '../annual/selectors';
import { Autocomplete, ErrorMessageBox, Outlet, RadioGroup } from '../common';
import { DailyProvider, useDailyContext } from '../daily/context';
import { PageBody } from '../layout/styled';
import { DISPLAY_MODE_OPTIONS, REPORT_OPTIONS } from './constants';
import { useReportOutletContext, useReportType } from './hooks';
import { useAddScenarioAPI, useDisplayModeParams, useExistMultiskillMode, useReportAPI, useReportEffectivePermission } from './selectors';
export var DisplayMode;
(function (DisplayMode) {
  DisplayMode["All"] = "ALL";
  DisplayMode["Single"] = "SINGLE_SKILL";
  DisplayMode["Multi"] = "MULTI_SKILL";
})(DisplayMode || (DisplayMode = {}));
var SCENARIO_MAXIMUM = 6;
var isDailyReport = function (reportType) {
  return reportType === REPORT_ROUTER_URL.Daily || reportType === REPORT_ROUTER_URL.Interval;
};
var setDailySelectionParam = function (params, key, value) {
  if (value === undefined) {
    params.delete(key);
  } else {
    params.set(key, "".concat(value));
  }
};
export var ReportControl = function (props) {
  var _a;
  var reportData = useReportAPI()[0];
  var _b = useDisplayModeParams(),
    displayMode = _b[0],
    setDisplayMode = _b[1];
  var navigate = useNavigate();
  var location = useLocation();
  var dailyState = props.dailyState;
  var reportType = useReportType();
  var isDisabledDaily = !((_a = reportData.data) === null || _a === void 0 ? void 0 : _a.hasDaily) || !!props.disableDaily;
  var existMultiskillMode = useExistMultiskillMode();
  var supportsDisplayMode = reportType === REPORT_ROUTER_URL.Annual || reportType === REPORT_ROUTER_URL.Period;
  var showDisplayMode = supportsDisplayMode && existMultiskillMode;
  var t = useTranslation(['report', 'translation']).t;
  useEffect(function () {
    if (reportData.data && isDisabledDaily && (reportType === REPORT_ROUTER_URL.Daily || reportType === REPORT_ROUTER_URL.Interval)) {
      navigate(REPORT_ROUTER_URL.Annual);
    }
  }, [isDisabledDaily, navigate, reportData.data, reportType]);
  return _jsxs(Stack, __assign({
    direction: "row",
    gap: 5,
    alignItems: "center",
    paddingBottom: 4
  }, {
    children: [props.children, showDisplayMode && _jsx(Autocomplete, {
      sx: {
        marginLeft: 'auto',
        width: 200
      },
      "data-testid": "displayMode",
      value: displayMode,
      onChange: setDisplayMode,
      options: DISPLAY_MODE_OPTIONS,
      getOptionLabel: function (o) {
        return t(o.name);
      }
    }), _jsx(Autocomplete, {
      sx: {
        marginLeft: showDisplayMode ? '' : 'auto',
        width: 200
      },
      "data-testid": "reportType",
      value: reportType,
      onChange: function (route) {
        var params = new URLSearchParams(location.search);
        if (isDailyReport(reportType) && isDailyReport(route) && dailyState) {
          params.set('uniqueId', dailyState.uniqueId);
          setDailySelectionParam(params, 'chartLobId', dailyState.chartLobId);
          setDailySelectionParam(params, 'chartSubLobId', dailyState.chartSubLobId);
          setDailySelectionParam(params, 'chartQueueId', dailyState.chartQueueId);
        }
        var nextReportSupportsDisplayMode = route === REPORT_ROUTER_URL.Annual || route === REPORT_ROUTER_URL.Period;
        if (nextReportSupportsDisplayMode && existMultiskillMode) {
          params.set('displayMode', displayMode);
        } else {
          params.delete('displayMode');
        }
        var queryString = params.toString();
        navigate(route + (queryString ? "?".concat(queryString) : ''));
      },
      options: reportData.data ? REPORT_OPTIONS : [],
      getOptionLabel: function (o) {
        var _a;
        return t(o["name".concat((_a = reportData.data) === null || _a === void 0 ? void 0 : _a.planType)]) || t(o.name);
      },
      getOptionDisabled: function (o) {
        return (o.id === REPORT_ROUTER_URL.Daily || o.id === REPORT_ROUTER_URL.Interval) && isDisabledDaily;
      },
      loading: reportData.requesting
    })]
  }));
};
export var ScenarioDifferenceToggle = function (_a) {
  var differenceMode = _a.differenceMode,
    setDifferenceMode = _a.setDifferenceMode;
  var t = useTranslation('report').t;
  var reportType = useReportType();
  var displayMode = useDisplayModeParams()[0];
  var summaryChunkSize = useSummaryChunkSize();
  if (reportType !== REPORT_ROUTER_URL.Annual) return null;
  if (displayMode === DisplayMode.All || summaryChunkSize <= 1) return null;
  return _jsxs(_Fragment, {
    children: [_jsx(Typography, __assign({
      variant: "subtitle2",
      whiteSpace: "nowrap"
    }, {
      children: t('resourcing.showScenarioDiff')
    })), _jsxs(RadioGroup, __assign({
      row: true
    }, {
      children: [_jsx(FormControlLabel, {
        onClick: function () {
          return setDifferenceMode === null || setDifferenceMode === void 0 ? void 0 : setDifferenceMode(DifferenceMode.Absolute);
        },
        checked: differenceMode === DifferenceMode.Absolute,
        control: _jsx(Radio, {}),
        label: t('resourcing.absoluteValue')
      }), _jsx(FormControlLabel, {
        onClick: function () {
          return setDifferenceMode === null || setDifferenceMode === void 0 ? void 0 : setDifferenceMode(DifferenceMode.Percentage);
        },
        checked: differenceMode === DifferenceMode.Percentage,
        control: _jsx(Radio, {}),
        label: t('resourcing.percentages')
      })]
    }))]
  });
};
var AnnualControl = function (_a) {
  var _b, _c, _d;
  var differenceMode = _a.differenceMode,
    setDifferenceMode = _a.setDifferenceMode;
  var t = useTranslation('report').t;
  var reportType = useReportType();
  var _e = useAddScenarioAPI(),
    addScenarioData = _e[0],
    addScenario = _e[1],
    clearAddedData = _e[2];
  var _f = useReportAPI(),
    reportData = _f[0],
    fetchReport = _f[1];
  var _g = useSummaryReportAPI(),
    fetchSummaryData = _g[1];
  var capacityPermission = useReportEffectivePermission();
  var hasAddPermission = capacityPermission === StatusPermission.Edit && reportData.data && reportData.data.scenarios.length < SCENARIO_MAXIMUM;
  var copyError = ((_c = (_b = addScenarioData.data) === null || _b === void 0 ? void 0 : _b.actualCopyOperation) === null || _c === void 0 ? void 0 : _c.status) === 'failed' ? [{
    message: (_d = addScenarioData.data.actualCopyOperation.errorMessage) !== null && _d !== void 0 ? _d : t('resourcing.addScenarioCopyFailed')
  }] : [];
  useEffect(function () {
    var _a, _b;
    if (((_b = (_a = addScenarioData.data) === null || _a === void 0 ? void 0 : _a.actualCopyOperation) === null || _b === void 0 ? void 0 : _b.status) === 'completed') {
      fetchReport();
      fetchSummaryData(true);
    }
    return clearAddedData;
  }, [addScenarioData.data, clearAddedData, fetchReport, fetchSummaryData]);
  var handleAddScenario = function () {
    addScenario();
  };
  if (reportType !== REPORT_ROUTER_URL.Annual) return null;
  return _jsxs(_Fragment, {
    children: [_jsx(ErrorMessageBox, {
      errors: copyError
    }), _jsx(Button, __assign({
      variant: "contained",
      sx: {
        whiteSpace: 'nowrap'
      },
      disabled: !!addScenarioData.requesting || !hasAddPermission,
      onClick: handleAddScenario
    }, {
      children: t('resourcing.addScenario')
    })), _jsx(ScenarioDifferenceToggle, {
      differenceMode: differenceMode,
      setDifferenceMode: setDifferenceMode
    })]
  });
};
var ResourcingContent = function () {
  var context = useReportOutletContext();
  var _a = useState(DifferenceMode.Absolute),
    differenceMode = _a[0],
    setDifferenceMode = _a[1];
  var dailyState = useDailyContext()[0];
  return _jsxs(PageBody, {
    children: [_jsx(ReportControl, __assign({
      dailyState: dailyState
    }, {
      children: _jsx(AnnualControl, {
        differenceMode: differenceMode,
        setDifferenceMode: setDifferenceMode
      })
    })), _jsx(Outlet, {
      context: __assign(__assign({}, context), {
        isPercentages: differenceMode
      })
    })]
  });
};
export var ResourcingLayout = function () {
  return _jsx(DailyProvider, {
    children: _jsx(ResourcingContent, {})
  });
};